/**
 * Generate wallet deep links for mobile wallet connections
 * 
 * NOTE: WalletConnect protocol is now the PRIMARY method for mobile wallet connections.
 * These deep link functions are kept for potential future use or as fallbacks.
 * 
 * For mobile wallets (especially Kraken and Trust Wallet), use WalletConnect modal
 * which shows QR codes and handles app-to-app connections properly.
 * 
 * Deep links may still work for Phantom and Solflare, but WalletConnect is preferred
 * for better compatibility and user experience.
 */

export interface WalletDeepLink {
  name: string;
  url: string;
  icon?: string;
}

/**
 * Generate Phantom wallet deep link
 * Phantom expects the full URL with https://
 */
export function getPhantomDeepLink(domain: string): string {
  // Ensure domain has https://
  const fullUrl = domain.startsWith('http') ? domain : `https://${domain}`;
  return `https://phantom.app/ul/browse/${encodeURIComponent(fullUrl)}`;
}

/**
 * Generate Solflare wallet deep link
 * Solflare uses a different format - needs full URL with https://
 * Updated to use the correct format that opens browser view
 */
export function getSolflareDeepLink(domain: string): string {
  // Ensure domain has https://
  const fullUrl = domain.startsWith('http') ? domain : `https://${domain}`;
  // Solflare browser format - use the browse endpoint without ref parameter
  // Format: solflare.com/ul/v1/browse/{encoded_url}
  return `https://solflare.com/ul/v1/browse/${encodeURIComponent(fullUrl)}`;
}

/**
 * Generate Kraken Wallet (OKX) deep link
 * 
 * NOTE: Kraken deep links are unreliable. Use WalletConnect protocol instead.
 * This function is kept for potential future use but is not actively used.
 */
export function getKrakenDeepLink(domain: string): string {
  // Ensure domain has https://
  const fullUrl = domain.startsWith('http') ? domain : `https://${domain}`;
  // OKX/Kraken Wallet - try custom scheme format
  // Format: okx://wallet/dapp/details?dappUrl=...
  // If that doesn't work, fallback to universal link
  if (typeof window !== 'undefined') {
    // Try custom scheme first (works on Android)
    const customScheme = `okx://wallet/dapp/details?dappUrl=${encodeURIComponent(fullUrl)}`;
    // Also provide universal link as alternative
    const universalLink = `https://www.okx.com/wallet/connect?dappUrl=${encodeURIComponent(fullUrl)}`;
    // Return custom scheme for now, can be changed based on testing
    return customScheme;
  }
  // Fallback
  return `https://www.okx.com/wallet/connect?dappUrl=${encodeURIComponent(fullUrl)}`;
}

/**
 * Generate Trust Wallet deep link
 * 
 * NOTE: Trust Wallet deep links may not work properly for Solana dApps.
 * Use WalletConnect protocol instead for reliable mobile connections.
 * This function is kept for potential future use but is not actively used.
 */
export function getTrustWalletDeepLink(domain: string): string {
  // Ensure domain has https://
  const fullUrl = domain.startsWith('http') ? domain : `https://${domain}`;
  // Trust Wallet deep link format - try simpler format without coin_id
  // Some versions may not support coin_id parameter properly
  // Format: link.trustwallet.com/open_url?url=...
  return `https://link.trustwallet.com/open_url?url=${encodeURIComponent(fullUrl)}`;
}

/**
 * Get all available wallet deep links
 * 
 * NOTE: This function is used to display wallet options in the UI.
 * However, clicking these wallets now opens WalletConnect modal instead of
 * using deep links. The URLs are kept for display purposes only.
 */
export function getAllWalletDeepLinks(domain: string): WalletDeepLink[] {
  // Ensure domain has https://
  const fullUrl = domain.startsWith('http') ? domain : `https://${domain}`;
  
  return [
    {
      name: 'Phantom',
      url: getPhantomDeepLink(fullUrl),
      icon: '👻',
    },
    {
      name: 'Solflare',
      url: getSolflareDeepLink(fullUrl),
      icon: '🔥',
    },
    {
      name: 'Kraken',
      url: getKrakenDeepLink(fullUrl),
      icon: '🦑',
    },
    {
      name: 'Trust Wallet',
      url: getTrustWalletDeepLink(fullUrl),
      icon: '🛡️',
    },
  ];
}

/**
 * Try alternative deep link formats for wallets that might not work with primary format
 */
export function getAlternativeKrakenLink(domain: string): string {
  const fullUrl = domain.startsWith('http') ? domain : `https://${domain}`;
  // Alternative: Universal link format
  return `https://www.okx.com/wallet/connect?dappUrl=${encodeURIComponent(fullUrl)}`;
}

/**
 * Detect if running on mobile device
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

/**
 * Detect if running in Telegram WebApp
 * More reliable check: must have WebApp object AND initData (actually in Telegram)
 */
export function isTelegramWebApp(): boolean {
  if (typeof window === 'undefined') return false;
  const tg = (window as any).Telegram?.WebApp;
  return !!(tg && tg.initData); // Must have initData to be actually in Telegram
}

/**
 * Detect if running in a wallet browser (Phantom, Solflare, Kraken, Trust Wallet, etc.)
 */
export function isWalletBrowser(): boolean {
  if (typeof window === 'undefined') return false;
  
  // Check for Solana wallet provider (most reliable indicator)
  const solana = (window as any).solana;
  if (solana) return true;
  
  // Check user agent for wallet browsers
  const ua = navigator.userAgent.toLowerCase();
  // Trust Wallet might be detected as "trustwallet" or "trust" in user agent
  // Also check for wallet-specific patterns
  const walletPatterns = [
    'phantom',
    'solflare',
    'kraken',
    'okx',
    'trust',
    'trustwallet',
    'wallet', // Generic wallet identifier (many wallets include this)
  ];
  
  // Check if user agent contains any wallet pattern
  if (walletPatterns.some(pattern => ua.includes(pattern))) {
    return true;
  }
  
  // Check referrer - if we came from a wallet deep link, we're in a wallet browser
  const referrer = document.referrer.toLowerCase();
  if (referrer && (
    referrer.includes('phantom.app') ||
    referrer.includes('solflare.com') ||
    referrer.includes('trustwallet.com') ||
    referrer.includes('link.trustwallet.com') ||
    referrer.includes('okx.com')
  )) {
    return true;
  }
  
  return false;
}

/**
 * Get the specific wallet name if we're in a wallet browser
 */
export function getWalletBrowserName(): string | null {
  if (typeof window === 'undefined') return null;
  
  const ua = navigator.userAgent.toLowerCase();
  const referrer = document.referrer.toLowerCase();
  
  if (ua.includes('phantom') || referrer.includes('phantom.app')) return 'Phantom';
  if (ua.includes('solflare') || referrer.includes('solflare.com')) return 'Solflare';
  if (ua.includes('kraken') || ua.includes('okx') || referrer.includes('okx.com')) return 'Kraken';
  if (ua.includes('trust') || referrer.includes('trustwallet.com')) return 'Trust Wallet';
  
  // Check for solana provider
  const solana = (window as any).solana;
  if (solana) {
    if (solana.isPhantom) return 'Phantom';
    if (solana.isSolflare) return 'Solflare';
    if (solana.isTrust) return 'Trust Wallet';
  }
  
  return null;
}
